import { Trans } from "@lingui/react/macro";
import { Button, ButtonProps } from "@silversea-ssc/ui-components-react";
import React, { ReactElement } from "react";
import { ABTestDomAttr } from "src/ab-test-experiments/client/use-experiment-tag";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import { isPresent } from "src/utils/checks";
import { match } from "ts-pattern";
import { useAlgoliaConversionTracking } from "./Algolia/algoliaAnalytics";

type Props = {
  url: ECommerceStatus | undefined;
  raqUrl: string | undefined;
  size: ButtonProps["size"];
  className?: string;
  disableSecondaryRAQ?: boolean;
  inverted?: boolean;
};

const ECommerceButtons: React.FC<Props> = ({
  url = { type: "disabled" },
  raqUrl,
  size,
  disableSecondaryRAQ = false,
  className,
  inverted = false
}) => {
  const { onECommerceClick, onRAQClick } = useAlgoliaConversionTracking();

  if (url.type == "loading") return null;

  return match(url)
    .with({ type: "disabled" }, () => (
      <RAQButton
        url={raqUrl}
        variant="accent"
        size={size}
        className={className}
        inverted={inverted}
        onClick={onRAQClick}
      />
    ))
    .with({ type: "ecommerce" }, ({ ecommerceUrl }) => {
      return (
        <>
          <ECommerceButton
            url={ecommerceUrl}
            size={size}
            className={className}
            variant="accent"
            inverted={inverted}
            onClick={onECommerceClick}
          />

          {!disableSecondaryRAQ && (
            <RAQButton
              url={raqUrl}
              variant="text"
              size={size}
              className={className}
              inverted={inverted}
              onClick={onRAQClick}
            />
          )}
        </>
      );
    })
    .with({ type: "priceconfig" }, ({ url }) => {
      return (
        <React.Fragment>
          <PriceConfigButton
            url={url}
            size={size}
            className={className}
            variant="accent"
            inverted={inverted}
            onClick={onECommerceClick}
          />
          {!disableSecondaryRAQ && (
            <RAQButton
              url={raqUrl}
              variant="text"
              size={size}
              className={className}
              inverted={inverted}
              onClick={onRAQClick}
            />
          )}
        </React.Fragment>
      );
    })
    .exhaustive();
};

export default ECommerceButtons;

type NewEcommerceButtonProps = Omit<
  Props,
  "enableExperimentRAQtoPriceConfig" | "disableSecondaryRAQ"
> & {
  variant?: ButtonProps["variant"];
  overrideEcommerceLabel?: ReactElement;
  disableRAQ?: boolean;
  inverted?: boolean;
} & Partial<ABTestDomAttr>;

export const NewEcommerceButton: React.FC<NewEcommerceButtonProps> = ({
  raqUrl,
  size,
  url = { type: "disabled" },
  variant = "primary",
  className,
  overrideEcommerceLabel,
  disableRAQ = false,
  ...props
}) => {
  const { onECommerceClick, onRAQClick } = useAlgoliaConversionTracking();
  if (url.type == "loading") return null;

  return match(url)
    .with({ type: "disabled" }, () =>
      disableRAQ ? null : (
        <RAQButton
          url={raqUrl}
          variant={variant}
          size={size}
          className={className}
          onClick={onRAQClick}
          {...props}
        />
      )
    )
    .with({ type: "ecommerce" }, ({ ecommerceUrl }) => (
      <ECommerceButton
        url={ecommerceUrl}
        size={size}
        className={className}
        variant={variant}
        overrideLabel={overrideEcommerceLabel}
        onClick={onECommerceClick}
        {...props}
      />
    ))
    .with({ type: "priceconfig" }, ({ url }) => (
      <PriceConfigButton
        url={url}
        size={size}
        className={className}
        variant={variant}
        onClick={onECommerceClick}
        {...props}
      />
    ))
    .exhaustive();
};

type EcommerceButtonProps = {
  size: ButtonProps["size"];
  variant: ButtonProps["variant"];
  className?: string;
  url?: string;
  overrideLabel?: ReactElement;
  inverted?: boolean;
  onClick?: () => void;
} & Partial<ABTestDomAttr>;

const ECommerceButton: React.FC<EcommerceButtonProps> = ({
  size,
  className,
  url,
  variant,
  overrideLabel,
  onClick,
  ...props
}) => {
  return (
    <Button
      variant={variant}
      href={url}
      dataContentPiece="book_your_cruise"
      noWrap
      size={size}
      className={className}
      onClick={onClick}
      onAuxClick={onClick}
      {...props}
    >
      {overrideLabel ? (
        <React.Fragment>{overrideLabel}</React.Fragment>
      ) : (
        <Trans id="ecommerce-button-label">Book your Cruise</Trans>
      )}
    </Button>
  );
};

const PriceConfigButton: React.FC<EcommerceButtonProps> = ({
  size,
  className,
  url,
  variant,
  ...props
}) => {
  return (
    <Button
      variant={variant}
      href={url}
      dataContentPiece="plan_your_cruise"
      noWrap
      size={size}
      className={className}
      {...props}
    >
      <Trans id="priceconfig-button-label">Plan your Cruise</Trans>
    </Button>
  );
};

const RAQButton: React.FC<EcommerceButtonProps> = ({
  size,
  className,
  url,
  variant,
  ...props
}) => {
  if (!isPresent(url)) return null;

  return (
    <Button
      variant={variant}
      href={url}
      dataContentPiece="request_a_quote"
      size={size}
      className={className}
      {...props}
    >
      <Trans>Request a Quote</Trans>
    </Button>
  );
};
